import { colorscales } from './colorscales';

// Extract legend data from first layer with boundaryPlace.colorScale
export function extractLegendData(layerCollection) {
    if (!layerCollection || !Array.isArray(layerCollection)) {
        return null;
    }

    const layerWithBoundary = layerCollection.find(layer => layer.boundaryPlace?.colorScale);

    if (layerWithBoundary) {
        const colorScalePriceRanges = Object.values(layerWithBoundary.boundaryPlace.colorScale);
        const numRanges = colorScalePriceRanges.length;
        const colorScaleLevels = colorscales[numRanges]?.levels || [];

        return {
            colorScalePriceRanges,
            colorScaleLevels,
            layerName: layerWithBoundary.name,
            boundaryId: layerWithBoundary.boundaryPlace.id,
        };
    }

    return null;
}

// Convert AVIV colorScale to array of price thresholds for legacy template
export function colorScaleToLegends(colorScalePriceRanges) {
    return colorScalePriceRanges
        .map(range => {
            if (Array.isArray(range)) {
                // Format: [minPrice, maxPrice] or [color, maxPrice]
                return range[range.length - 1];
            }
            if (typeof range === 'object' && range !== null) {
                // Format: { min, max } or { color, price }
                return range.max || range.price || range[1];
            }
            return range;
        })
        .filter(maxPrice => typeof maxPrice === 'number');
}

function formatLegendPrice(price) {
    if (typeof price !== 'number') {
        return '?';
    }

    // Show one decimal for small prices (rent), integers for large prices (sell)
    if (price < 100) {
        return price.toFixed(1);
    }
    return String(Math.round(price));
}

function rgbaToSolid(color) {
    const rgbaMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/);
    if (rgbaMatch) {
        return `rgb(${rgbaMatch[1]}, ${rgbaMatch[2]}, ${rgbaMatch[3]})`;
    }
    return color;
}

function buildLegendHtml(legends, colors, colorscaleLength, description = '') {
    const descriptionHtml = description
        ? `<div class="vector-map__legend-description">${description}</div>`
        : '';

    // Build color bar items
    const colorItems = colors
        .map((color, index) => {
            let title;
            const classNames = [`q${index + 1}_${colorscaleLength}`];

            if (index === 0) {
                classNames.push('q-first');
                title = `&lt; ${formatLegendPrice(legends[0])} €`;
            } else if (index === colorscaleLength - 1) {
                classNames.push('q-last');
                title = `&gt; ${formatLegendPrice(legends[legends.length - 1])} €`;
            } else {
                title = `${formatLegendPrice(legends[index - 1])} € à ${formatLegendPrice(
                    legends[index],
                )} €`;
            }

            const solidColor = rgbaToSolid(color);
            return `<li class="${classNames.join(
                ' ',
            )} tip-top" title="${title}" style="background-color: ${solidColor};"></li>`;
        })
        .join('');

    const lowerBound =
        legends.length > 0
            ? `<li class="lbound">&lt; ${formatLegendPrice(legends[0])} &euro;</li>`
            : '';

    const upperBound =
        legends.length > 0
            ? `<li class="ubound">&gt; ${formatLegendPrice(
                  legends[legends.length - 1],
              )} &euro;</li>`
            : '';

    return `
        ${descriptionHtml}
        <ul class="colorscale aviv-colorscale">
            ${lowerBound}
            ${colorItems}
            ${upperBound}
        </ul>
    `;
}

export function updateAvivLegend($mapLegend, legendData, description) {
    if (!$mapLegend || !legendData) {
        return;
    }

    const { colorScalePriceRanges, colorScaleLevels } = legendData;

    const legends = colorScaleToLegends(colorScalePriceRanges);

    if (legends.length === 0) {
        $mapLegend.hide();
        return;
    }

    const avivLegendSelector = '[data-legend-aviv]';
    let $avivLegend = $mapLegend.find(avivLegendSelector);

    if ($avivLegend.length === 0) {
        $avivLegend = $('<div />', { 'data-legend-aviv': '' });
        $mapLegend.append($avivLegend);
    }

    const colorscaleLength = colorScaleLevels.length;
    const html = buildLegendHtml(legends, colorScaleLevels, colorscaleLength, description);

    $avivLegend.html(html);
    $avivLegend.show();
    $mapLegend.show();

    $mapLegend
        .children()
        .not(avivLegendSelector)
        .hide();
}

export function hideAvivLegend($mapLegend) {
    if (!$mapLegend) return;

    const $avivLegend = $mapLegend.find('[data-legend-aviv]');
    if ($avivLegend.length > 0) {
        $avivLegend.hide();
    }
}

export default {
    extractLegendData,
    colorScaleToLegends,
    updateAvivLegend,
    hideAvivLegend,
};
